import React, { useState, useCallback, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import MappingFilter from '../../components/MappingFilter/MappingFilter';
import DiagramV2 from './diagramv2';
import DiagramService from '../../services/diagramService';
import { DiagramData } from '../../types/diagramData';
import { IMappingFilterData } from '../../types/mappingFilterData';

const DiagramViewV2: React.FC = () => {
  const { authState } = useOktaAuth();
  
  // Create a stable reference to prevent recreation
  const diagramServiceRef = useRef<DiagramService | null>(null);
  if (!diagramServiceRef.current) {
    diagramServiceRef.current = new DiagramService();
  }
  const diagramService = diagramServiceRef.current;
  
  const [selectedFilters, setSelectedFilters] = useState<IMappingFilterData>({
    areaId: "0",
    apiStructure: null,
    developerProgram: null,
    managedBy: null,
    name: null,
  });
  
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [data, setData] = useState<DiagramData>({apis: [], areas: []});
  const [treeData, setTreeData] = useState<any>(null);


  // Convert API data to tree structure for D3 - Memoized to prevent unnecessary recalculations
  const convertAPIDataToTree = useCallback((diagramData: DiagramData) => {
    const root: any = {
      name: "",
      children: []
    };

    // Create a map to track unique areas by their id to avoid duplicates
    const areaMap = new Map();
    
    // Build areas from APIs data to avoid duplication
    diagramData.apis.forEach(api => {
      if (api.areas && api.areas.length > 0) {
        api.areas.forEach(area => {
          if (!areaMap.has(area.id)) {
            const areaNode = {
              name: area.name,
              id: area.id,
              level: parseInt(area.level),
              type: 'area',
              children: [],
              apis: [] // Store APIs that belong to this area
            };
            areaMap.set(area.id, areaNode);
          }
        });
      }
    });

    // Build the area hierarchy graph based on API relationships
    const areaGraph = new Map();
    
    // Initialize all areas in the graph
    Array.from(areaMap.values()).forEach(area => {
      areaGraph.set(area.id, {
        ...area,
        children: [],
        parents: new Set(), // Use Set to avoid duplicate parents
        level: area.level
      });
    });

    // Analyze API relationships to determine area hierarchy
    diagramData.apis.forEach(api => {
      if (api.areas && api.areas.length > 1) {
        // Sort areas by level to establish hierarchy
        const sortedAreas = [...api.areas].sort((a, b) => parseInt(a.level) - parseInt(b.level));
        
        // Build parent-child relationships based on consecutive levels
        for (let i = 0; i < sortedAreas.length - 1; i++) {
          const parentArea = areaGraph.get(sortedAreas[i].id);
          const childArea = areaGraph.get(sortedAreas[i + 1].id);
          
          if (parentArea && childArea) {
            // Add child to parent if not already present
            if (!parentArea.children.find((child: any) => child.id === childArea.id)) {
              parentArea.children.push(childArea);
            }
            // Add parent to child
            childArea.parents.add(parentArea.id);
          }
        }
      }
    });

    // Build the final tree structure
    const addedToTree = new Set();
    
    // Add level 1 areas (root level) to the tree
    Array.from(areaGraph.values())
      .filter(area => area.level === 1)
      .forEach(area => {
        root.children.push(area);
        addedToTree.add(area.id);
      });

    // Add APIs to their most specific areas (highest level)
    diagramData.apis.forEach(api => {
      if (api.areas && api.areas.length > 0) {
        // Find the most specific area (highest level) for this API
        const targetArea = api.areas.reduce((prev, current) => {
          return (parseInt(current.level) > parseInt(prev.level)) ? current : prev;
        });
        
        const areaNode = areaGraph.get(targetArea.id);
        if (areaNode) {
          const apiNode = {
            name: api.fullApiName,
            type: 'api',
            developerProgram: api.developerProgram,
            id: api.id,
            description: api.apiDescription,
            latestApiVersionNumber: api.latestApiVersionNumber
          };
          areaNode.apis.push(apiNode);
          areaNode.children.push(apiNode);
        }
      }
    });

    return root;
  }, []); // Empty dependency array since function logic doesn't depend on external variables

  const handleFilterChange = useCallback(async (selectedValues: IMappingFilterData) => {
    setSelectedFilters(selectedValues);
    setIsLoading(true);

    try {
      const diagramData = await diagramService.get(selectedValues, authState);
      setData(diagramData);
      const convertedTreeData = convertAPIDataToTree(diagramData);
      setTreeData(convertedTreeData);
      
    } catch (error) {
      console.error("Error fetching diagram data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [authState, diagramService, convertAPIDataToTree]);

  const handleFilterInitialized = useCallback(async (initialFilters: IMappingFilterData) => {
    console.log("Diagram filters initialized with:", initialFilters);
    setIsInitializing(false);
    
    // The data loading will be handled by handleFilterChange which gets called 
    // by the MappingFilter's onChange with the initial filters (URL or defaults)
    console.log("Diagram filter initialization complete, data loading will be handled by onChange");
  }, []);

  // Remove the old useEffect - now handled by filter initialization

  return (
    <div className="diagram-view" style={{
      height: 'calc(100vh - 84px)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ flexShrink: 0 }}>
        <MappingFilter onChange={handleFilterChange} onInitialized={handleFilterInitialized} />
      </div>

      <div className="content" style={{ flex: 1 }}>
        {isInitializing ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <div>Initializing filters...</div>
          </div>
        ) : isLoading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <div>Loading diagram data...</div>
          </div>
        ) : (
          treeData && <DiagramV2 data={treeData} />
        )}
      </div>
    </div>
  );
};

export default DiagramViewV2;
